/**
 * Facebook Pixel Utility Helper
 * Pixel ID: 1543157840891973
 */

// Safe helper to invoke window.fbq
export const fbq = (...args) => {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq(...args);
    }
};

/**
 * Standard PageView event
 */
export const trackPageView = () => {
    fbq('track', 'PageView');
};

/**
 * Track ViewContent when a user views a product page
 * @param {Object} product
 */
export const trackViewContent = (product, currency = 'LKR') => {
    if (!product) return;
    fbq('track', 'ViewContent', {
        content_name: product.name,
        content_ids: [product._id || product.id],
        content_type: 'product',
        content_category: product.category?.name || product.category || 'Clothing',
        value: product.price || 0,
        currency: currency
    });
};

/**
 * Track AddToCart when a user adds a product to the cart
 * @param {Object} product
 * @param {number} quantity
 * @param {string} currency
 */
export const trackAddToCart = (product, quantity = 1, currency = 'LKR') => {
    if (!product) return;
    fbq('track', 'AddToCart', {
        content_name: product.name || product.title,
        content_ids: [product._id || product.productId || product.id],
        content_type: 'product',
        value: (product.price || 0) * (quantity || 1),
        currency: currency,
        num_items: quantity || 1
    });
};

/**
 * Track AddToWishlist when a user adds a product to the wishlist
 * @param {Object} product
 * @param {string} currency
 */
export const trackAddToWishlist = (product, currency = 'LKR') => {
    if (!product) return;
    fbq('track', 'AddToWishlist', {
        content_name: product.name,
        content_ids: [product._id || product.id],
        content_type: 'product',
        value: product.price || 0,
        currency: currency
    });
};

/**
 * Track InitiateCheckout when a user begins checkout
 * @param {Array} items
 * @param {number} totalValue
 * @param {string} currency
 */
export const trackInitiateCheckout = (items = [], totalValue = 0, currency = 'LKR') => {
    fbq('track', 'InitiateCheckout', {
        content_ids: items.map(item => item.productId || item._id || item.id),
        content_type: 'product',
        num_items: items.reduce((acc, item) => acc + (item.quantity || 1), 0),
        value: totalValue || 0,
        currency: currency
    });
};

/**
 * Track Purchase when an order is completed
 * @param {string} orderId
 * @param {number} totalValue
 * @param {string} currency
 * @param {Array} items
 */
export const trackPurchase = (orderId, totalValue = 0, currency = 'LKR', items = []) => {
    fbq('track', 'Purchase', {
        content_ids: items.length > 0 ? items.map(item => item.productId || item._id || item.id) : [orderId],
        content_type: 'product',
        value: totalValue,
        currency: currency,
        order_id: orderId
    });
};

/**
 * Track Search when a user searches products
 * @param {string} searchQuery
 */
export const trackSearch = (searchQuery) => {
    if (!searchQuery) return;
    fbq('track', 'Search', {
        search_string: searchQuery
    });
};

/**
 * Track Contact when user submits contact form or support inquiry
 */
export const trackContact = () => {
    fbq('track', 'Contact');
};

/**
 * Track Lead (e.g. newsletter sign up, registration)
 */
export const trackLead = (category = 'general') => {
    fbq('track', 'Lead', {
        content_category: category
    });
};

/**
 * Track custom event
 * @param {string} eventName
 * @param {Object} params
 */
export const trackCustomEvent = (eventName, params = {}) => {
    fbq('trackCustom', eventName, params);
};
